import React from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import CircularProgress from '@mui/material/CircularProgress';
import { usePartnerScope, PartnerOption } from '../contexts/PartnerScopeContext';

/**
 * Partner selector for internal users (SOPHIA-35095). Renders nothing for
 * partner users — their scope is fixed by the token claim.
 */
export function PartnerSwitcher(): React.ReactElement | null {
  const { isInternal, activePartnerId, setSelectedPartnerId, partners, partnersLoading } =
    usePartnerScope();

  if (!isInternal) return null;

  // The scope list includes inactive partners (for name resolution elsewhere);
  // only active ones are valid switch targets.
  const activePartners = partners.filter((p) => p.active !== false);
  const value: PartnerOption | null = activePartners.find((p) => p.id === activePartnerId) ?? null;

  return (
    <Autocomplete
      size="small"
      data-testid="partner-switcher"
      options={activePartners}
      value={value}
      loading={partnersLoading}
      getOptionLabel={(option) => option.name}
      isOptionEqualToValue={(option, selected) => option.id === selected.id}
      onChange={(_event, option) => setSelectedPartnerId(option?.id ?? null)}
      sx={{ width: 280, bgcolor: '#fff', borderRadius: 1 }}
      renderInput={(params) => (
        <TextField
          {...params}
          label="Viewing partner"
          placeholder="Select a partner"
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {partnersLoading ? <CircularProgress color="inherit" size={16} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
}
